/**
 * PhaseSelector Component
 *
 * A horizontal tab bar showing workflow phases (Planning, Execution, Post-Campaign, Admin).
 * Features:
 * - Horizontal row of phase tabs on desktop
 * - Dropdown selector on mobile for compact display
 * - Active tab highlighted with brand color
 * - Click to switch phases (updates adminEditorStore.currentPhase)
 * - Uses custom phase names from adminEditorStore.customPhaseNames
 * - Phase icons for visual identification
 * - Breadcrumb navigation (Home / Campaign / Tab) on desktop
 * - Phase tabs dynamically centered under the TopBar search bar
 *
 * Design: Mobile-first (dropdown on mobile, tabs on desktop)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Compass, PlayCircle, CheckCircle2, Settings, ChevronDown } from 'lucide-react';
import { useAdminEditorStore } from '@features/admin/store/adminEditorStore';
import { DEFAULT_PHASE_NAMES, PHASE_ORDER } from '@features/admin/constants/defaults';
import type { PhaseType } from '@features/admin/types/adminEditor';
import { BreadcrumbCampaignSelector } from './BreadcrumbCampaignSelector';


/**
 * Get the icon component for a phase type
 */
const PhaseIcon: React.FC<{ type: PhaseType; size?: number }> = ({ type, size = 14 }) => {
  switch (type) {
    case 'planning':
      return <Compass size={size} />;
    case 'execution':
      return <PlayCircle size={size} />;
    case 'post-campaign':
      return <CheckCircle2 size={size} />;
    case 'admin':
      return <Settings size={size} />;
    default:
      return <Compass size={size} />;
  }
};

/**
 * Get display name for a phase, considering custom names
 */
const getPhaseDisplayName = (
  type: PhaseType,
  customNames: Partial<Record<PhaseType, string>>
): string => {
  return customNames[type] || DEFAULT_PHASE_NAMES[type];
};

export const PhaseSelector: React.FC = () => {
  const { currentPhase, customPhaseNames, setCurrentPhase } = useAdminEditorStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [tabsLeft, setTabsLeft] = useState<number | null>(null);

  // Measure search bar center and position phase tabs to match
  const alignToSearchBar = useCallback(() => {
    const searchBar = document.querySelector('input[placeholder*="Search"]');
    const row = rowRef.current;
    const tabs = tabsRef.current;
    if (!searchBar || !row || !tabs) return;

    const searchRect = searchBar.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const tabsWidth = tabs.offsetWidth;

    // Search bar center relative to the PhaseSelector row
    const searchCenterInRow = (searchRect.left + searchRect.width / 2) - rowRect.left;
    // Position tabs so their center aligns with the search bar center
    const newLeft = searchCenterInRow - (tabsWidth / 2);
    setTabsLeft(newLeft);
  }, []);

  // Align on mount and window resize
  useEffect(() => {
    alignToSearchBar();
    // Re-align on resize since TopBar content widths may change
    window.addEventListener('resize', alignToSearchBar);
    // Also re-align after a brief delay for initial render settling
    const timer = setTimeout(alignToSearchBar, 100);
    return () => {
      window.removeEventListener('resize', alignToSearchBar);
      clearTimeout(timer);
    };
  }, [alignToSearchBar]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle phase selection
  const handlePhaseClick = (phase: PhaseType) => {
    // Toggle off if clicking the same phase, otherwise set new phase
    if (currentPhase === phase) {
      setCurrentPhase(null);
    } else {
      setCurrentPhase(phase);
    }
    setIsDropdownOpen(false);
  };

  // Get currently selected phase name for mobile dropdown
  const selectedPhaseName = currentPhase
    ? getPhaseDisplayName(currentPhase, customPhaseNames)
    : 'All Phases';

  return (
    <>
      {/* Desktop: Full layout with phase tabs and campaign selector */}
      <div ref={rowRef} className="hidden md:flex items-center w-full relative">
        {/* Center: Phase Tabs - absolutely positioned to align with TopBar search bar */}
        <div
          ref={tabsRef}
          className="absolute flex items-center z-0"
          style={{
            left: tabsLeft != null ? `${tabsLeft}px` : '50%',
            transform: tabsLeft != null ? undefined : 'translateX(-50%)',
            top: 0,
            bottom: 0,
          }}
        >
          <div className="flex items-center gap-1 bg-dark-surface/20 backdrop-blur-sm rounded-xl p-1 border border-dark-border/15">
            {/* "All" option to show all tabs */}
            <button
              onClick={() => setCurrentPhase(null)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200
                ${currentPhase === null
                  ? 'bg-gradient-to-r from-brand-500 to-brand-400 text-white shadow-sm shadow-brand-500/20'
                  : 'text-dark-muted hover:text-white hover:bg-dark-surface/60'
                }
              `}
              title="Show all tabs"
              aria-label="Show all tabs"
              aria-pressed={currentPhase === null}
              data-testid="phase-selector-all"
            >
              All
            </button>

            {/* Phase tabs */}
            {PHASE_ORDER.map((phase) => {
              const isActive = currentPhase === phase;
              const displayName = getPhaseDisplayName(phase, customPhaseNames);

              return (
                <button
                  key={phase}
                  onClick={() => handlePhaseClick(phase)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200
                    ${isActive
                      ? 'bg-gradient-to-r from-brand-500 to-brand-400 text-white shadow-sm shadow-brand-500/20'
                      : 'text-dark-muted hover:text-white hover:bg-dark-surface/60'
                    }
                  `}
                  title={displayName}
                  aria-label={`${displayName} phase`}
                  aria-pressed={isActive}
                  data-testid={`phase-selector-${phase}`}
                >
                  <PhaseIcon type={phase} size={14} />
                  <span className="hidden lg:inline">{displayName}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right: Campaign & Route Selector */}
        <div className="ml-auto flex items-center text-xs shrink-0 z-10">
          <BreadcrumbCampaignSelector
            className="mx-0"
            showRouteSelector={true}
          />
        </div>
      </div>

      {/* Mobile: Dropdown selector */}
      <div className="md:hidden relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className={`
            flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150
            ${currentPhase
              ? 'bg-brand-500 text-white'
              : 'bg-dark-surface/50 text-dark-text hover:bg-dark-surface'
            }
          `}
          aria-label="Select phase"
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          data-testid="phase-selector-mobile-trigger"
        >
          {currentPhase && <PhaseIcon type={currentPhase} size={14} />}
          <span>{selectedPhaseName}</span>
          <ChevronDown
            size={14}
            className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown menu */}
        {isDropdownOpen && (
          <div
            className="absolute top-full left-0 mt-1 w-48 bg-dark-surface/95 backdrop-blur-lg border border-dark-border/30 rounded-xl shadow-2xl shadow-black/30 z-50 overflow-hidden"
            role="listbox"
            aria-label="Phase options"
          >
            {/* All option */}
            <button
              onClick={() => handlePhaseClick(null as unknown as PhaseType)}
              className={`
                flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-all duration-150
                ${currentPhase === null
                  ? 'bg-brand-500/20 text-brand-500'
                  : 'text-dark-text hover:bg-dark-surface/50'
                }
              `}
              role="option"
              aria-selected={currentPhase === null}
            >
              All Phases
            </button>

            {/* Phase options */}
            {PHASE_ORDER.map((phase) => {
              const isActive = currentPhase === phase;
              const displayName = getPhaseDisplayName(phase, customPhaseNames);

              return (
                <button
                  key={phase}
                  onClick={() => handlePhaseClick(phase)}
                  className={`
                    flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-all duration-150
                    ${isActive
                      ? 'bg-brand-500/20 text-brand-500'
                      : 'text-dark-text hover:bg-dark-surface/50'
                    }
                  `}
                  role="option"
                  aria-selected={isActive}
                >
                  <PhaseIcon type={phase} size={14} />
                  <span>{displayName}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
